import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import InstitutionCard from "@/components/InstitutionCard";
import { api } from "@/lib/api";
import { Search } from "lucide-react";

/**
 * Shared index page for /community-colleges and /universities.
 * The `variant` prop selects filters + copy.
 */
export default function InstitutionIndex({ variant = "community_college" }) {
  const isCC = variant === "community_college";
  const heading = isCC ? "Florida Community Colleges" : "Florida State Universities";
  const eyebrow = isCC ? "The Feeder System" : "The Destinations";
  const subhead = isCC
    ? "Every Florida community college is a legal transfer partner to the SUS. What differentiates them is how they advise, which universities they feed, and how coherent their prerequisite scaffolding is."
    : "Twelve public universities in Florida — from UF and FSU to the emerging campuses. Every one of them accepts Florida AA transfers, but the strategic reality differs sharply across them.";
  const endpoint = isCC ? "/community-colleges" : "/universities";
  const basePath = isCC ? "/community-colleges" : "/universities";
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");

  useEffect(() => {
    setLoading(true);
    api.get(endpoint, { params: { q, page_size: 100 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [q, endpoint]);

  const handleSearch = (val) => {
    setQ(val);
    if (val) setParams({ q: val }); else setParams({});
  };

  return (
    <Layout>
      <SEO
        title={`${heading} — Transfer Directory | Beyond Valencia`}
        description={subhead}
        canonical={basePath}
      />
      <section className="bg-bone border-b border-stone/30" data-testid={`${isCC ? "cc" : "uni"}-index-page`}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
          <div className="text-[11px] uppercase tracking-[0.24em] text-brass font-medium">{eyebrow}</div>
          <h1 className="mt-5 font-serif font-medium text-4xl sm:text-5xl lg:text-6xl text-ivy tracking-tight leading-[1.05]" data-testid="institution-index-headline">
            {heading}
          </h1>
          <p className="mt-6 max-w-3xl text-base sm:text-lg text-ink-soft leading-relaxed">{subhead}</p>
          <div className="mt-10 flex items-center border border-stone/40 bg-bone-dim/40 max-w-lg" data-testid="institution-search">
            <Search className="h-4 w-4 text-stone ml-3 flex-shrink-0" />
            <input
              type="search"
              placeholder={`Search ${isCC ? "community colleges" : "universities"}…`}
              value={q}
              onChange={(e) => handleSearch(e.target.value)}
              className="flex-1 bg-transparent border-0 outline-none px-3 py-2.5 text-sm text-ink placeholder-stone"
            />
          </div>
        </div>
      </section>

      <section className="bg-bone-dim/30" data-testid="institution-index-grid-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 lg:py-16">
          {loading ? (
            <p className="text-sm text-stone">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-stone italic">No matches. Try a different search.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="institution-index-grid">
              {items.map((inst, i) => (
                <motion.div key={inst.slug} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <InstitutionCard institution={inst} basePath={basePath} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
